import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Checkbox } from "../components/Checkbox";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, } from "../components/Dropdown";
import { Icon } from "../components/Icon";
import { IconButton } from "../components/IconButton";
import { LoadMore } from "../components/LoadMore";
import { Pagination } from "../components/Pagination";
import { radioIndicatorStyles, radioStyles, } from "../components/RadioGroup";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { Tooltip } from "../components/Tooltip";
import { useCopyToClipboard } from "../hooks";
import { ArrowDown, ArrowUp, ChevronSelectorVertical, Clipboard, ClipboardCheck, DotsHorizontal, } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useEffect, useLayoutEffect, useRef, useState, } from "react";
import { breakpoints, useWindowSize } from "./WindowUtility";
const cellHeight = "h-12";
function shouldRenderColumn(windowWidth, breakpoint) {
    if (!breakpoint) {
        return true;
    }
    return windowWidth >= breakpoints[breakpoint];
}
const ROW_REVEAL_DURATION_MS = 300;
/**
 * Reveals appended rows by animating the table's height, so the rows slide into
 * view at exactly the rate the footer below them moves down. Animating the
 * footer instead would let the rows pop in ahead of it.
 */
function useRowRevealAnimation(rowCount, enabled) {
    const ref = useRef(null);
    const previousHeightRef = useRef(null);
    const previousRowCountRef = useRef(rowCount);
    // Keep the last laid-out height current through every layout change, not just
    // row changes. Cells are skipped on the first commit (the window size is not
    // measured yet), so a height recorded only on mount would be the height of a
    // table with no columns, and the first reveal would animate from ~nothing.
    // ResizeObserver callbacks run after layout effects, so the value read below
    // is always the height from before the new rows landed.
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element || !enabled) {
            return;
        }
        previousHeightRef.current = element.scrollHeight;
        const observer = new ResizeObserver(() => {
            previousHeightRef.current = element.scrollHeight;
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [enabled]);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        // `scrollHeight` reports the content height even while we pin `height`
        // mid-animation, so this stays correct if rows land back to back.
        const height = element.scrollHeight;
        const previousHeight = previousHeightRef.current;
        const grew = rowCount > previousRowCountRef.current;
        previousRowCountRef.current = rowCount;
        if (!enabled ||
            !grew ||
            previousHeight === null ||
            previousHeight >= height ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        const clear = () => {
            element.style.transition = "";
            element.style.height = "";
            element.style.overflow = "";
        };
        element.style.overflow = "hidden";
        element.style.transition = "none";
        element.style.height = `${previousHeight}px`;
        const frame = requestAnimationFrame(() => {
            element.style.transition = `height ${ROW_REVEAL_DURATION_MS}ms ease-out`;
            element.style.height = `${height}px`;
        });
        element.addEventListener("transitionend", clear, { once: true });
        return () => {
            cancelAnimationFrame(frame);
            element.removeEventListener("transitionend", clear);
            clear();
        };
    }, [rowCount, enabled]);
    return ref;
}
/**
 * A tabular data display built on TanStack Table, with text filtering, client-
 * or server-side sorting, pagination or a "Load more" footer, and row
 * selection, rendered with the DataTable.* cell helpers. Use it to list
 * structured records (data sources, members, files); for very long or infinite
 * server-side datasets, prefer ScrollableDataTable, which virtualizes rows and
 * loads more on scroll.
 * @summary Sortable, filterable, paginated data table.
 */
export function DataTable({ data, totalRowCount, rowCountIsCapped = false, columns, className, widthClassName = "w-full", filter, filterColumn, columnsBreakpoints = {}, pagination, setPagination, onLoadMore, isLoadingMore = false, onShowLess, sorting, setSorting, isServerSideSorting = false, disablePaginationNumbers = false, rowSelection, setRowSelection, enableRowSelection = false, enableMultiRowSelection = true, getRowId, enableSortingRemoval = true, hideRowDivider = false, disableRowClickSelection = false, }) {
    const windowSize = useWindowSize();
    const [columnFilters, setColumnFilters] = useState([]);
    const isServerSidePagination = !!totalRowCount && totalRowCount > data.length;
    const isClientSideSortingEnabled = !isServerSideSorting && !isServerSidePagination;
    const onPaginationChange = pagination && setPagination
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(pagination) : updater;
            setPagination(newValue);
        }
        : undefined;
    const onSortingChange = sorting && setSorting
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(sorting) : updater;
            setSorting(newValue);
        }
        : undefined;
    const onRowSelectionChange = rowSelection && setRowSelection
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(newValue);
        }
        : undefined;
    const table = useReactTable({
        data,
        columns,
        rowCount: totalRowCount,
        manualPagination: isServerSidePagination,
        manualSorting: isServerSideSorting,
        ...(isServerSideSorting && {
            onSortingChange: onSortingChange,
        }),
        enableSortingRemoval,
        getCoreRowModel: getCoreRowModel(),
        ...(!isServerSideSorting && {
            getSortedRowModel: getSortedRowModel(),
            enableSorting: isClientSideSortingEnabled,
        }),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
        onColumnFiltersChange: setColumnFilters,
        ...(enableRowSelection && {
            onRowSelectionChange,
        }),
        state: {
            columnFilters,
            ...(isServerSideSorting && {
                sorting,
            }),
            pagination,
            ...(enableRowSelection && { rowSelection }),
        },
        initialState: {
            sorting,
        },
        onPaginationChange,
        enableRowSelection,
        enableMultiRowSelection,
        getRowId,
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: table is recreated every render, adding it would cause infinite re-runs
    useEffect(() => {
        var _a;
        if (filterColumn) {
            (_a = table.getColumn(filterColumn)) === null || _a === void 0 ? void 0 : _a.setFilterValue(filter);
        }
    }, [filter, filterColumn]);
    const rows = table.getRowModel().rows;
    // Uses the rendered row count, not `data.length`, so filtering keeps the
    // measured height in sync.
    const rowRevealRef = useRowRevealAnimation(rows.length, !!onLoadMore && !pagination);
    return (React.createElement("div", { className: cn("flex flex-col gap-2", className, widthClassName) },
        React.createElement(DataTable.Root, { containerRef: rowRevealRef },
            React.createElement(DataTable.Header, null, table.getHeaderGroups().map((headerGroup) => (React.createElement(DataTable.Row, { key: headerGroup.id, widthClassName: widthClassName }, headerGroup.headers.map((header) => {
                var _a, _b;
                const breakpoint = columnsBreakpoints[header.id];
                if (!windowSize.width ||
                    !shouldRenderColumn(windowSize.width, breakpoint)) {
                    return null;
                }
                return (React.createElement(DataTable.Head, { column: header.column, key: header.id, onClick: header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined, className: cn(header.column.getCanSort() && "cursor-pointer") },
                    React.createElement("div", { className: cn("flex items-center space-x-1 whitespace-nowrap", ((_a = header.column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.headerAlign) === "right"
                            ? "justify-end"
                            : ((_b = header.column.columnDef.meta) === null || _b === void 0 ? void 0 : _b.headerAlign) ===
                                "center"
                                ? "justify-center"
                                : undefined) },
                        flexRender(header.column.columnDef.header, header.getContext()),
                        header.column.getCanSort() && (React.createElement(Icon, { visual: header.column.getIsSorted() === "asc"
                                ? ArrowUp
                                : header.column.getIsSorted() === "desc"
                                    ? ArrowDown
                                    : ChevronSelectorVertical, size: "xs", className: "ml-1" })))));
            }))))),
            React.createElement(DataTable.Body, null, rows.map((row) => {
                const handleRowClick = () => {
                    var _a, _b;
                    if (enableRowSelection && row.getCanSelect()) {
                        row.toggleSelected(!enableMultiRowSelection ? true : undefined);
                    }
                    (_b = (_a = row.original).onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                };
                return (React.createElement(DataTable.Row, { widthClassName: widthClassName, key: row.id, hideBottomBorder: hideRowDivider, onClick: enableRowSelection && !disableRowClickSelection
                        ? handleRowClick
                        : row.original.onClick, onDoubleClick: row.original.onDoubleClick, rowData: row.original, ...(enableRowSelection && {
                        "data-selected": row.getIsSelected(),
                    }) }, row.getVisibleCells().map((cell) => {
                    const breakpoint = columnsBreakpoints[cell.column.id];
                    if (!windowSize.width ||
                        !shouldRenderColumn(windowSize.width, breakpoint)) {
                        return null;
                    }
                    return (React.createElement(DataTable.Cell, { column: cell.column, key: cell.id }, flexRender(cell.column.columnDef.cell, cell.getContext())));
                })));
            }))),
        pagination && (React.createElement("div", { className: "p-1" },
            React.createElement(Pagination, { size: "xs", pagination: table.getState().pagination, setPagination: table.setPagination, rowCount: table.getRowCount(), rowCountIsCapped: rowCountIsCapped, disablePaginationNumbers: disablePaginationNumbers }))),
        !pagination && onLoadMore && (React.createElement("div", { className: "p-1" },
            React.createElement(LoadMore, { onLoadMore: onLoadMore, onShowLess: onShowLess, isLoading: isLoadingMore, rowCount: data.length, totalRowCount: totalRowCount, totalRowCountIsCapped: rowCountIsCapped })))));
}
// cellHeight in pixels
const COLUMN_HEIGHT = 48;
const MIN_COLUMN_WIDTH = 40;
/**
 * A virtualized variant of DataTable for large or infinite datasets: rows are
 * windowed with TanStack Virtual inside a scrollable container, with a sticky
 * header and infinite loading via onLoadMore. Use it when row counts are too
 * large for pagination; for ordinary lists prefer DataTable.
 * @summary Virtualized, infinitely scrollable data table.
 */
export function ScrollableDataTable({ data, totalRowCount, columns, className, widthClassName = "w-full", columnsBreakpoints = {}, maxHeight, onLoadMore, sorting, setSorting, isLoading = false, rowSelection, setRowSelection, enableRowSelection, enableMultiRowSelection = true, getRowId, containerRef, hideRowDivider = false, disableRowClickSelection = false, }) {
    const windowSize = useWindowSize();
    const loadMoreRef = useRef(null);
    const scrollSentinelRef = useRef(null);
    const [tableWidth, setTableWidth] = useState(0);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const tableContainerRef = useRef(null);
    const isSorting = !!setSorting;
    // Handle container ref
    const setRef = (element) => {
        tableContainerRef.current = element;
        if (containerRef) {
            if (typeof containerRef === "function") {
                containerRef(element);
            }
            else if ("current" in containerRef) {
                containerRef.current = element;
            }
        }
    };
    // Monitor table width changes
    useEffect(() => {
        if (!tableContainerRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setTableWidth(entry.contentRect.width);
            }
        });
        resizeObserver.observe(tableContainerRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    const onSortingChange = sorting && setSorting
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(sorting) : updater;
            setSorting(newValue);
        }
        : undefined;
    const onRowSelectionChange = rowSelection && setRowSelection
        ? (updater) => {
            const newValue = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(newValue);
        }
        : undefined;
    const table = useReactTable({
        data,
        columns,
        rowCount: totalRowCount,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        ...(enableRowSelection && {
            onRowSelectionChange,
        }),
        state: {
            ...(enableRowSelection && { rowSelection }),
            ...(isSorting && {
                sorting,
            }),
        },
        manualSorting: isSorting,
        ...(isSorting && {
            onSortingChange: onSortingChange,
        }),
        enableSortingRemoval: true,
        enableRowSelection,
        enableMultiRowSelection,
        getRowId,
    });
    useEffect(() => {
        if (!tableContainerRef.current || !table || !tableWidth) {
            return;
        }
        const columns = table.getAllColumns();
        // Calculate ideal widths and handle minimums
        const idealSizing = columns.reduce((acc, column) => {
            var _a;
            const ratio = ((_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.sizeRatio) || 0;
            const calculated = Math.max(Math.floor((ratio / 100) * tableWidth), MIN_COLUMN_WIDTH);
            return { ...acc, [column.id]: calculated };
        }, {});
        // Ensure total width matches tableWidth
        const totalIdealWidth = Object.values(idealSizing).reduce((a, b) => a + b, 0);
        const widthDifference = tableWidth - totalIdealWidth;
        // adjust the largest column with leftover size
        if (widthDifference !== 0) {
            const adjustColumnId = Object.entries(idealSizing).sort((a, b) => b[1] - a[1])[0][0];
            idealSizing[adjustColumnId] += widthDifference;
        }
        table.setColumnSizing(idealSizing);
    }, [table, tableWidth]);
    // Get the current column sizing from the table for rendering
    const columnSizing = table.getState().columnSizing;
    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => COLUMN_HEIGHT,
    });
    // Intersection observer for infinite loading
    useEffect(() => {
        if (!onLoadMore || !loadMoreRef.current) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            // retrieving the sentinel div
            if (entries[0].isIntersecting && !isLoading) {
                onLoadMore();
            }
        }, {
            root: tableContainerRef.current,
            rootMargin: "200% 0% 0% 0%",
            threshold: 0.1,
        });
        observer.observe(loadMoreRef.current);
        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
            observer.disconnect();
        };
    }, [onLoadMore, isLoading]);
    // Observe whether the bottom of the table is visible to show/hide scroll indicator
    useEffect(() => {
        const sentinel = scrollSentinelRef.current;
        const root = tableContainerRef.current;
        if (!sentinel || !root) {
            return;
        }
        const observer = new IntersectionObserver(([entry]) => setCanScrollDown(!entry.isIntersecting), { root, threshold: 0.1 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);
    return (React.createElement("div", { className: cn("relative overflow-y-auto overflow-x-hidden", className, widthClassName, maxHeight === true
            ? "flex-1"
            : typeof maxHeight === "string"
                ? maxHeight
                : "max-h-100"), ref: setRef },
        React.createElement("div", { className: "relative" },
            React.createElement(DataTable.Root, { className: "w-full table-fixed" },
                React.createElement(DataTable.Header, { className: "sticky top-0 z-20 bg-background shadow-sm" }, table.getHeaderGroups().map((headerGroup) => (React.createElement(DataTable.Row, { key: headerGroup.id, widthClassName: widthClassName }, headerGroup.headers.map((header) => {
                    const breakpoint = columnsBreakpoints[header.id];
                    if (!windowSize.width ||
                        !shouldRenderColumn(windowSize.width, breakpoint)) {
                        return null;
                    }
                    return (React.createElement(DataTable.Head, { column: header.column, key: header.id, onClick: isSorting && header.column.getCanSort()
                            ? header.column.getToggleSortingHandler()
                            : undefined, className: cn("max-w-0", header.column.getCanSort() &&
                            isSorting &&
                            "cursor-pointer"), style: {
                            width: columnSizing[header.id],
                            minWidth: columnSizing[header.id],
                        } },
                        React.createElement("div", { className: "flex w-full items-center space-x-1 whitespace-nowrap" },
                            React.createElement("span", { className: "truncate" }, flexRender(header.column.columnDef.header, header.getContext())),
                            isSorting && header.column.getCanSort() && (React.createElement(Icon, { visual: header.column.getIsSorted() === "asc"
                                    ? ArrowUp
                                    : header.column.getIsSorted() === "desc"
                                        ? ArrowDown
                                        : ChevronSelectorVertical, size: "xs", className: "ml-1" })))));
                }))))),
                React.createElement(DataTable.Body, { className: "relative w-full", style: {
                        height: `${rowVirtualizer.getTotalSize()}px`,
                    } }, rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    const handleRowClick = () => {
                        var _a, _b;
                        if (enableRowSelection && row.getCanSelect()) {
                            row.toggleSelected(!enableMultiRowSelection ? true : undefined);
                        }
                        (_b = (_a = row.original).onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                    };
                    return (React.createElement(DataTable.Row, { key: row.id, id: row.id, widthClassName: widthClassName, hideBottomBorder: hideRowDivider, onClick: enableRowSelection && !disableRowClickSelection
                            ? handleRowClick
                            : row.original.onClick, onDoubleClick: row.original.onDoubleClick, rowData: row.original, className: "absolute w-full", ...(enableRowSelection && {
                            "data-selected": row.getIsSelected(),
                        }), style: {
                            transform: `translateY(${virtualRow.start}px)`,
                        } }, row.getVisibleCells().map((cell) => {
                        const breakpoint = columnsBreakpoints[cell.column.id];
                        if (!windowSize.width ||
                            !shouldRenderColumn(windowSize.width, breakpoint)) {
                            return null;
                        }
                        return (React.createElement(DataTable.Cell, { column: cell.column, key: cell.id, id: cell.id, className: "max-w-0", style: {
                                width: columnSizing[cell.column.id],
                                minWidth: columnSizing[cell.column.id],
                            } },
                            React.createElement("div", { className: "flex items-center space-x-1" },
                                React.createElement("span", { className: "truncate" }, flexRender(cell.column.columnDef.cell, cell.getContext())))));
                    })));
                }))),
            React.createElement("div", { ref: loadMoreRef, className: "absolute bottom-0 h-1 w-full" }),
            React.createElement("div", { ref: scrollSentinelRef, className: "h-px" })),
        React.createElement("div", { className: cn("pointer-events-none sticky -bottom-px left-0 right-0 -mt-10 h-10 bg-linear-to-t", "from-background via-background/60 to-transparent transition-opacity duration-300", canScrollDown ? "opacity-100" : "opacity-0") }),
        isLoading && (React.createElement("div", { className: "sticky bottom-0 left-0 right-0 flex justify-center bg-background/80 py-2 backdrop-blur-sm" },
            React.createElement("div", { className: "flex items-center gap-2 text-sm text-muted-foreground" },
                React.createElement(Spinner, { size: "xs" }),
                React.createElement("span", null, "Loading more data..."))))));
}
/** The underlying table element with its container-query wrapper. */
DataTable.Root = function DataTableRoot({ children, className, containerClassName, containerProps, containerRef, ...props }) {
    return (React.createElement("div", { ref: containerRef, className: cn("@container/table", containerClassName), ...containerProps },
        React.createElement("table", { className: cn("w-full table-fixed border-collapse", className), ...props }, children)));
};
/** Table head section (thead). */
DataTable.Header = function Header({ children, className, ...props }) {
    return (React.createElement("thead", { className: cn(className), ...props }, children));
};
/** Header cell (th) with alignment and optional tooltip from the column meta. */
DataTable.Head = function Head({ children, className, column, ...props }) {
    var _a, _b, _c, _d;
    return (React.createElement("th", { className: cn("heading-xs py-2 px-2 capitalize", ((_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.headerAlign) === "right"
            ? "text-right"
            : ((_b = column.columnDef.meta) === null || _b === void 0 ? void 0 : _b.headerAlign) === "center"
                ? "text-center"
                : "text-left", "text-foreground", (_c = column.columnDef.meta) === null || _c === void 0 ? void 0 : _c.className, className), ...props }, ((_d = column.columnDef.meta) === null || _d === void 0 ? void 0 : _d.tooltip) ? (React.createElement(Tooltip, { label: column.columnDef.meta.tooltip, trigger: children })) : (children)));
};
/** Table body section (tbody). */
DataTable.Body = function Body({ children, className, ...props }) {
    return (React.createElement("tbody", { className: className, ...props }, children));
};
/** Table row (tr) with hover/selection styling and a right-click context menu when rowData.menuItems is set. */
DataTable.Row = function Row({ children, className, onClick, onDoubleClick, widthClassName, rowData, hideBottomBorder = false, ...props }) {
    var _a, _b;
    const [contextMenuPosition, setContextMenuPosition] = useState(null);
    const handleContextMenu = (event) => {
        var _a;
        if (!((_a = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        event.preventDefault();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("tr", { className: cn("group/dt-row justify-center transition-colors duration-300 ease-out", !hideBottomBorder && ["border-b", "border-separator"], (onClick || onDoubleClick) &&
                "cursor-pointer [&:hover:not(:has(input:hover)):not(:has(button:hover))]:bg-muted-background", props["data-selected"] && "bg-muted-background/50", widthClassName, className), onClick: onClick || undefined, onDoubleClick: onDoubleClick || undefined, onContextMenu: handleContextMenu, ...props }, children),
        contextMenuPosition && ((_a = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(DropdownMenu, { open: !!contextMenuPosition, onOpenChange: (open) => !open && setContextMenuPosition(null), modal: true },
            React.createElement(DropdownMenuPortal, null,
                React.createElement(DropdownMenuContent, { align: "start", className: "whitespace-nowrap", style: {
                        position: "fixed",
                        left: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.x) || 0,
                        top: (contextMenuPosition === null || contextMenuPosition === void 0 ? void 0 : contextMenuPosition.y) || 0,
                    } },
                    React.createElement(DropdownMenuGroup, null, (_b = rowData === null || rowData === void 0 ? void 0 : rowData.menuItems) === null || _b === void 0 ? void 0 : _b.map((item, index) => renderMenuItem(item, index, () => setContextMenuPosition(null))))))))));
};
const preventMenuItemClickThrough = (event) => {
    // Prevent the subsequent click from reaching elements behind the menu when
    // it closes on pointer down (modal={false}).
    event.preventDefault();
};
// Shared menu rendering functions
const renderSubmenuItem = (item, index, onItemClick) => (React.createElement(DropdownMenuSub, { key: `${item.label}-${index}` },
    React.createElement(DropdownMenuSubTrigger, { label: item.label, disabled: item.disabled, onPointerDown: (event) => {
            event.stopPropagation();
        } }),
    React.createElement(DropdownMenuPortal, null,
        React.createElement(DropdownMenuSubContent, null, item.selectionMode === "checkbox" ? (item.items.map((subItem) => (React.createElement(DropdownMenuCheckboxItem, { key: subItem.id, label: subItem.name, description: subItem.description, checked: subItem.checked, onCheckedChange: (checked) => {
                if (!checked) {
                    return;
                }
                item.onSelect(subItem.id);
                onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick();
            }, onSelect: (event) => {
                event.preventDefault();
            } })))) : (React.createElement(ScrollArea, { className: "flex max-h-72 min-w-24 flex-col", hideScrollBar: true },
            item.items.map((subItem) => (React.createElement(DropdownMenuItem, { key: subItem.id, label: subItem.name, description: subItem.description, onPointerDown: preventMenuItemClickThrough, onClick: (event) => {
                    event.stopPropagation();
                    item.onSelect(subItem.id);
                    onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick();
                } }))),
            React.createElement(ScrollBar, { className: "py-0" })))))));
const renderRegularItem = (item, index, onItemClick) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { kind, ...itemProps } = item;
    return (React.createElement(DropdownMenuItem, { key: `item-${index}`, ...itemProps, onPointerDown: preventMenuItemClickThrough, onClick: (event) => {
            var _a;
            event.stopPropagation();
            (_a = itemProps.onClick) === null || _a === void 0 ? void 0 : _a.call(itemProps, event);
            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick();
        } }));
};
const renderMenuItem = (item, index, onItemClick) => {
    switch (item.kind) {
        case "submenu":
            return renderSubmenuItem(item, index, onItemClick);
        case "item":
            return renderRegularItem(item, index, onItemClick);
    }
};
/** "..." row-actions button that opens a dropdown of menuItems. */
DataTable.MoreButton = function MoreButton({ className, menuItems, dropdownMenuProps, disabled, }) {
    const [open, setOpen] = useState(false);
    if (!(menuItems === null || menuItems === void 0 ? void 0 : menuItems.length)) {
        return null;
    }
    const { onOpenChange: dropdownOnOpenChange, ...restDropdownMenuProps } = dropdownMenuProps !== null && dropdownMenuProps !== void 0 ? dropdownMenuProps : {};
    const closeMenu = () => {
        setOpen(false);
    };
    return (React.createElement(DropdownMenu, { open: open, onOpenChange: (nextOpen) => {
            setOpen(nextOpen);
            dropdownOnOpenChange === null || dropdownOnOpenChange === void 0 ? void 0 : dropdownOnOpenChange(nextOpen);
        }, modal: false, ...restDropdownMenuProps },
        React.createElement(DropdownMenuTrigger, { onClick: (event) => {
                event.stopPropagation();
            }, onPointerDown: (event) => {
                event.stopPropagation();
            }, asChild: true },
            React.createElement(Button, { icon: DotsHorizontal, size: "icon", variant: "ghost-secondary", disabled: disabled, className: cn(disabled && "cursor-not-allowed opacity-50", className) })),
        React.createElement(DropdownMenuContent, { align: "end", hidden: disabled },
            React.createElement(DropdownMenuGroup, null, menuItems.map((item, index) => renderMenuItem(item, index, closeMenu))))));
};
/** Body cell (td) with truncation and column meta styling. */
DataTable.Cell = function Cell({ children, className, column, ...props }) {
    var _a;
    return (React.createElement("td", { className: cn(cellHeight, "truncate px-2", (_a = column.columnDef.meta) === null || _a === void 0 ? void 0 : _a.className, className), ...props }, children));
};
/** Standard cell layout with optional avatar, avatar stack, icon, and trailing description. */
DataTable.CellContent = function CellContent({ children, className, avatarUrl, avatarTooltipLabel, roundedAvatar, icon, iconClassName, description, grow = false, disabled, avatarStack, ...props }) {
    return (React.createElement("div", { className: cn("flex items-center", grow ? "flex-grow" : "", disabled && "cursor-not-allowed opacity-50", className), "aria-disabled": disabled || undefined, ...props },
        avatarUrl && avatarTooltipLabel && (React.createElement(Tooltip, { trigger: React.createElement(Avatar, { visual: avatarUrl, size: "xs", className: "mr-2", isRounded: roundedAvatar !== null && roundedAvatar !== void 0 ? roundedAvatar : false }), label: avatarTooltipLabel })),
        avatarUrl && !avatarTooltipLabel && (React.createElement(Avatar, { visual: avatarUrl, size: "xs", className: "mr-2", isRounded: roundedAvatar !== null && roundedAvatar !== void 0 ? roundedAvatar : false })),
        avatarStack && (React.createElement(Avatar.Stack, { avatars: avatarStack.items, nbVisibleItems: avatarStack.nbVisibleItems, size: "xs" })),
        icon && (React.createElement(Icon, { visual: icon, size: "sm", className: cn("mr-2 text-foreground", iconClassName) })),
        React.createElement("div", { className: cn("flex shrink truncate items-center", grow ? "flex-grow" : "") },
            React.createElement("div", { className: cn(grow ? "flex-grow" : "", "truncate text-sm", "text-foreground") }, children),
            description && (React.createElement("span", { className: cn("pl-2 text-sm", "text-muted-foreground") }, description)))));
};
/** Simple muted text cell with an optional tooltip and hover copy-to-clipboard button. */
DataTable.BasicCellContent = function BasicCellContent({ label, tooltip, className, textToCopy, disabled, ...props }) {
    const [isCopied, copyToClipboard] = useCopyToClipboard();
    const handleCopy = async () => {
        const textToUse = textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(label);
        void copyToClipboard(new ClipboardItem({
            "text/plain": new Blob([String(textToUse)], {
                type: "text/plain",
            }),
        }));
    };
    return (React.createElement(React.Fragment, null, tooltip ? (React.createElement(Tooltip, { tooltipTriggerAsChild: true, trigger: React.createElement("div", { className: cn(cellHeight, "group flex items-center gap-2 text-sm", "text-muted-foreground", disabled && "cursor-not-allowed opacity-50", className), "aria-disabled": disabled || undefined, ...props },
            React.createElement("span", { className: "truncate" }, label),
            textToCopy && (React.createElement(Button, { icon: isCopied ? ClipboardCheck : Clipboard, className: "hidden group-hover:block", variant: "outline", onClick: async (e) => {
                    e.stopPropagation();
                    await handleCopy();
                }, size: "xs" }))), label: tooltip })) : (React.createElement("div", { className: cn(cellHeight, "group flex items-center gap-2 text-sm", "text-muted-foreground", disabled && "cursor-not-allowed opacity-50", className), "aria-disabled": disabled || undefined, ...props },
        React.createElement("span", { className: "truncate" }, label),
        textToCopy && (React.createElement(Button, { icon: isCopied ? ClipboardCheck : Clipboard, className: "hidden group-hover:block", variant: "outline", onClick: async (e) => {
                e.stopPropagation();
                await handleCopy();
            }, size: "xs" }))))));
};
/** Cell content with a persistent copy-to-clipboard icon button. */
DataTable.CellContentWithCopy = function CellContentWithCopy({ children, textToCopy, className, }) {
    const [isCopied, copyToClipboard] = useCopyToClipboard();
    const handleCopy = async () => {
        void copyToClipboard(new ClipboardItem({
            "text/plain": new Blob([textToCopy !== null && textToCopy !== void 0 ? textToCopy : String(children)], {
                type: "text/plain",
            }),
        }));
    };
    return (React.createElement("div", { className: cn("flex items-center space-x-2", className) },
        React.createElement("span", { className: "truncate" }, children),
        React.createElement(IconButton, { icon: isCopied ? ClipboardCheck : Clipboard, variant: "outline", onClick: async (e) => {
                e.stopPropagation();
                await handleCopy();
            }, size: "xs" })));
};
/** Table caption element. */
DataTable.Caption = function Caption({ children, className, ...props }) {
    return (React.createElement("caption", { className: className, ...props }, children));
};
/** Builds a checkbox column for multi-row selection, with an optional select-all header. */
export function createSelectionColumn({ hideSelectAll = false, } = {}) {
    return {
        id: "select",
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) => !hideSelectAll ? (React.createElement(Checkbox, { checked: table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                    ? "partial"
                    : false, onCheckedChange: (state) => {
                if (state === "indeterminate") {
                    return;
                }
                table.toggleAllRowsSelected(state);
            } })) : null,
        cell: ({ row }) => (React.createElement("div", { className: "flex h-full w-full items-center" },
            React.createElement(Checkbox, { checked: row.getIsSelected(), disabled: !row.getCanSelect(), onCheckedChange: (state) => {
                    if (state === "indeterminate") {
                        return;
                    }
                    row.toggleSelected(state);
                } }))),
        meta: {
            className: "w-10",
        },
    };
}
/** Builds a radio column for single-row selection. */
export function createRadioSelectionColumn() {
    return {
        id: "radio-select",
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        cell: ({ row }) => (React.createElement("div", { className: "flex h-full w-full items-center" },
            React.createElement("div", { className: cn(radioStyles(), row.getIsSelected() && "bg-muted/50", !row.getCanSelect() && "cursor-not-allowed opacity-50"), "aria-checked": row.getIsSelected(), role: "radio" }, row.getIsSelected() && React.createElement("div", { className: radioIndicatorStyles() })))),
        meta: {
            className: "w-10",
        },
    };
}
//# sourceMappingURL=DataTable.js.map